import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, ArrowLeft, ArrowRight, User, Briefcase, Code2, GraduationCap, Award, Star, BookOpen, Sparkles, Trophy, Target, BarChart3, Save, Undo2, Redo2, Layout, Maximize2, X, Palette, Download, TrendingUp, ChevronUp, AlignLeft, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { calculateJobMatchScore } from '@soVendor/utils/scoreCalculations';
import { normalizeResumeText } from '@soVendor/utils/pdfTextSanitizer';
import { reconstructStructuredResumeText } from '@soVendor/utils/promptBuilder';
import { parseResumeSections, bucketResumeSections, sortSectionsByCanonicalOrder, type ResumeSection } from './utils/parseResumeSections';
import {
  serializeSavedProfileResumeFile,
  type SavedProfileDesign,
} from './utils/savedProfileResumeFile';
import { extractJDStructure, analyzeGaps, type GapAnalysis } from '@soVendor/utils/jdAnalysis';
import { SectionContentWithFormatting, isBulletLine, bulletContent, renderInlineFormatting } from './utils/sectionContentRenderer';
import {
  STEP_WRAPPER_CLASS,
  STEP_MOTION_CLASS,
  STEP_CARD_CLASS,
  STEP_CARD_COMPACT_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_PRIMARY_CTA_CLASS,
} from './shared/stepLayout';
import { supabase } from '@soVendor/lib/supabase';

const KNOWN_SECTION_HEADERS_RESULTS = new Set([
  'PERSONAL INFORMATION','CONTACT INFORMATION','CONTACT','PROFESSIONAL','PROFESSIONAL SUMMARY',
  'SUMMARY','OBJECTIVE','WORK EXPERIENCE','PROFESSIONAL EXPERIENCE','EXPERIENCE','EMPLOYMENT',
  'EDUCATION','SKILLS','TECHNICAL SKILLS','KEY SKILLS','CERTIFICATIONS','CERTIFICATIONS & LICENSES',
  'LANGUAGES','AWARDS','AWARDS & HONORS','HONORS','PROJECTS','ADDITIONAL INFORMATION',
  'REFERENCES','PUBLICATIONS','RESEARCH','TRAINING','TRAINING & DEVELOPMENT','LEADERSHIP',
  'AFFILIATIONS','HOBBIES','HOBBIES & INTERESTS',
]);

/**
 * Strip stray ALL-CAPS section-header fragments that the LLM bleeds
 * from the start of the next section into the tail of the current one.
 */
function cleanLLMSectionContent(content: string): string {
  const lines = content.split('\n');
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') lines.pop();
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    const upper = last.toUpperCase();
    if (
      last.length > 0 &&
      last.length < 45 &&
      (KNOWN_SECTION_HEADERS_RESULTS.has(upper) ||
        (last === upper && /^[A-Z][A-Z\s&/\-:]+$/.test(upper)))
    ) {
      lines.pop();
    } else {
      break;
    }
  }
  return lines.join('\n').trim();
}

/** Section headers for fallback when not using section cards */
const RESUME_SECTION_HEADERS = [
  'professional summary', 'summary', 'objective', 'work experience', 'experience',
  'education', 'skills', 'technical skills', 'certifications', 'projects',
  'awards', 'honors', 'publications', 'additional information', 'references',
  'key achievements', 'core competencies', 'expertise', 'languages', 'contact'
];

function isSectionHeader(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 60) return false;
  const normalized = trimmed.replace(/:$/, '').trim().toLowerCase();
  return RESUME_SECTION_HEADERS.some((h) => normalized === h || normalized.startsWith(h + ' ') || normalized.endsWith(' ' + h));
}

/** Kickresume-style section card: header + content. Template skin applied via parent .resume-preview--* (see index.css). */
function SectionCard({ section, sectionIndex, onSectionClick, displayTitle }: { section: ResumeSection; sectionIndex?: number; onSectionClick?: (index: number) => void; displayTitle?: string }) {
  const title = (displayTitle ?? section.title).toUpperCase();
  const isClickable = typeof sectionIndex === 'number' && onSectionClick;
  const card = (
    <div className="resume-preview-section-card rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/50 overflow-hidden shadow-sm">
      <div className="resume-preview-section-header px-4 py-2.5 bg-gray-700 dark:bg-gray-800 border-b border-gray-600 dark:border-gray-700">
        <h4 className="text-sm font-semibold vscode-text-primary tracking-wide">{title}</h4>
      </div>
      <div className="resume-preview-section-body p-4">
        <SectionContentWithFormatting content={section.content} sectionTitle={section.title} />
      </div>
    </div>
  );
  if (isClickable) {
    return (
      <button
        type="button"
        onClick={() => onSectionClick(sectionIndex!)}
        className="w-full text-left rounded-xl border-2 border-transparent hover:border-[#6C47FF]/40 dark:hover:border-[#5B8DFF]/40 focus:border-[#6C47FF] dark:focus:border-[#5B8DFF] focus:outline-none focus:ring-2 focus:ring-[#6C47FF]/30 transition-colors group"
        title="Click to edit this section"
      >
        {card}
        <span className="block text-center text-xs vscode-text-tertiary py-1 opacity-0 group-hover:opacity-100 transition-opacity" data-i18n="optimizer.resultsStep.clickToEditSection">Click to edit this section</span>
      </button>
    );
  }
  return card;
}

/** Icon for section title (Kickresume-style left column) */
function sectionIcon(title: string) {
  const t = title.toLowerCase();
  if (t.includes('contact') || t.includes('personal') || t.includes('resume') || t.includes('profile') || t === 'summary' || t.includes('objective')) return User;
  if (t.includes('experience') || t.includes('employment') || t.includes('work')) return Briefcase;
  if (t.includes('skill') || t.includes('language') || t.includes('framework') || t.includes('cloud') || t.includes('technical')) return Code2;
  if (t.includes('education')) return GraduationCap;
  if (t.includes('certification') || t.includes('award') || t.includes('honor')) return Award;
  if (t.includes('achievement') || t.includes('strength') || t.includes('hobby')) return Star;
  if (t.includes('goal')) return Target;
  if (t.includes('project')) return BarChart3;
  return BookOpen;
}

/** First-section titles that should display as "Personal Information" (contact/profile block). */
const FIRST_SECTION_AS_PERSONAL = new Set([
  'resume', 'full resume', 'profile', 'professional summary', 'summary', 'objective',
  'contact', 'contact information', 'personal information'
]);
function isFirstSectionPersonalInfo(title: string): boolean {
  return FIRST_SECTION_AS_PERSONAL.has(title.toLowerCase().trim());
}

/** Suggested sections for "Add New Section" (Kickresume-style) */
const SUGGESTED_SECTIONS: { id: string; title: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'achievements', title: 'Achievements', Icon: Trophy },
  { id: 'awards', title: 'Awards', Icon: Award },
  { id: 'certificates', title: 'Certificates', Icon: BookOpen },
  { id: 'education', title: 'Education', Icon: GraduationCap },
  { id: 'goal', title: 'Goal', Icon: Target },
  { id: 'projects', title: 'Projects', Icon: BarChart3 },
  { id: 'hobbies', title: 'Hobbies', Icon: Star },
  { id: 'strengths', title: 'Strengths', Icon: Star },
];

/** Optional hint text per section type when expanded (Kickresume-style) */
const SECTION_HINTS: Record<string, string> = {
  'personal information': 'Add your name, contact details, and a short profile summary.',
  'contact': 'Add your name, contact details, and a short profile summary.',
  'profile': 'Summarize your experience and goals in a few sentences.',
  'professional summary': 'Summarize your experience and goals in a few sentences.',
  'summary': 'Summarize your experience and goals in a few sentences.',
  'work experience': 'List each role with company, dates, and bullet points for achievements.',
  'experience': 'List each role with company, dates, and bullet points for achievements.',
  'skills': 'List key skills, technologies, or competencies relevant to the role.',
  'education': 'Degree, institution, and graduation date for each entry.',
  'awards': 'Have you participated in a hackathon, marathon or competition? Mention it here.',
  'achievements': 'Use action verbs to highlight notable accomplishments.',
  'certificates': 'Show that you keep learning. List certifications and training.',
  'projects': 'Describe relevant projects with technologies used and outcomes.',
  'goal': 'State your career goal and how this role fits.',
  'hobbies': 'Brief interests that show personality and fit.',
  'strengths': 'Key strengths that align with the role.',
};
function getSectionHint(title: string): string | null {
  const key = title.toLowerCase().trim();
  if (SECTION_HINTS[key]) return SECTION_HINTS[key];
  const found = Object.keys(SECTION_HINTS).find(k => key.includes(k));
  return found ? SECTION_HINTS[found] : null;
}

/**
 * Each entry corresponds to exactly ONE real CSS skin class that the product
 * can actually render (both in-app preview and PDF download).
 * There are 9 distinct skins — no two-column rendering exists in the CSS.
 */
export interface TemplateConfig {
  id: string;
  label: string;
  ats: boolean;
  category: string;
  roles: string[];
  /** CSS skin class applied to resume-preview container */ skin: string;
  /** header background */   hBg: string;
  /** header text color */   hTx: string;
  /** accent / bullet color */ ac: string;
  /** document body background */ bodyBg: string;
  /**
   * How section cards are rendered — matches the actual CSS skin:
   *  rounded-accent  → 1px border in accent + box-shadow + rounded (blue-classic)
   *  left-accent     → 4px solid left border in accent, no full border (blue-modern / navy-compact / slate-pro)
   *  none            → no border, section header underlined only (blue-minimal)
   *  sharp           → 2px solid black border, sharp corners (black-bold)
   *  rounded-soft    → 1px gray border + soft shadow + rounded (black-clean)
   *  double-rule     → no card, section header uses double bottom-border (black-serif)
   *  mono-border     → no card, section header plain underline, monospace font (mono-clean)
   */
  cardStyle: 'rounded-accent' | 'left-accent' | 'none' | 'sharp' | 'rounded-soft' | 'double-rule' | 'mono-border';
  /** Font stack for the thumbnail preview */
  fontFamily: 'sans' | 'serif' | 'mono';
  /** If true, name is centered in the header (blue-minimal, black-serif) */
  centerName?: boolean;
}

/**
 * Templates covering all 9 library categories. Ordered: 4 featured first
 * (shown in the sidebar picker), rest accessible via "Browse Library".
 * Category values must exactly match LIBRARY_CATEGORIES filter strings.
 */
export const TEMPLATE_LIBRARY: TemplateConfig[] = [
  // ── Featured 4 ─────────────────────────────────────────────────────────────
  {
    id: 'ats-optimized', skin: 'blue-classic',  label: 'ATS Optimized',  ats: true,
    category: 'Finance',
    roles: ['Finance Analyst','Business Analyst','Project Manager','Operations','Strategy'],
    hBg: '#dbeafe', hTx: '#1e3a5f', ac: '#3b82f6', bodyBg: '#f8fbff',
    cardStyle: 'rounded-accent', fontFamily: 'sans',
  },
  {
    id: 'bold-dark',     skin: 'black-bold',    label: 'Classic Pro',    ats: true,
    category: 'Tech',
    roles: ['Software Engineer','Data Scientist','DevOps','QA Engineer','ML Engineer'],
    hBg: '#f3f4f6', hTx: '#111827', ac: '#6b7280', bodyBg: '#ffffff',
    cardStyle: 'sharp', fontFamily: 'sans',
  },
  {
    id: 'executive',     skin: 'black-clean',   label: 'Executive',      ats: true,
    category: 'Business',
    roles: ['Director','VP','C-Suite','Management Consultant','Senior Manager'],
    hBg: '#f9fafb', hTx: '#1f2937', ac: '#6b7280', bodyBg: '#ffffff',
    cardStyle: 'rounded-soft', fontFamily: 'sans',
  },
  {
    id: 'navy-tech',     skin: 'navy-compact',  label: 'Navy Tech',      ats: true,
    category: 'Tech',
    roles: ['Cloud Architect','DevOps Engineer','SRE','Backend Engineer','Senior IC'],
    hBg: '#eff6ff', hTx: '#1e3a5f', ac: '#3b82f6', bodyBg: '#f8fbff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  // ── Tech ───────────────────────────────────────────────────────────────────
  {
    id: 'modern-blue',   skin: 'blue-modern',   label: 'Product Manager', ats: true,
    category: 'Tech',
    roles: ['Product Manager','Tech Lead','Scrum Master','Engineering Manager','Program Manager'],
    hBg: '#eff6ff', hTx: '#1e40af', ac: '#60a5fa', bodyBg: '#f8fbff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'developer',     skin: 'mono-clean',    label: 'Developer',      ats: true,
    category: 'Tech',
    roles: ['Full Stack Developer','Open Source Dev','Freelance Developer','SRE','Indie Hacker'],
    hBg: '#f8fafc', hTx: '#1e293b', ac: '#64748b', bodyBg: '#ffffff',
    cardStyle: 'mono-border', fontFamily: 'mono',
  },
  // ── Business ───────────────────────────────────────────────────────────────
  {
    id: 'slate-pro',     skin: 'slate-pro',     label: 'Operations Pro', ats: true,
    category: 'Business',
    roles: ['Operations Manager','Program Manager','Business Analyst','Supply Chain','Coordinator'],
    hBg: '#f1f5f9', hTx: '#334155', ac: '#94a3b8', bodyBg: '#f8fafc',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'business-clean', skin: 'blue-classic', label: 'Business Clean', ats: true,
    category: 'Business',
    roles: ['Business Development','Sales Manager','Account Executive','Partnerships','Marketing Manager'],
    hBg: '#daeaf7', hTx: '#0f4c75', ac: '#0369a1', bodyBg: '#f4f9fd',
    cardStyle: 'rounded-accent', fontFamily: 'sans',
  },
  // ── Creative ───────────────────────────────────────────────────────────────
  {
    id: 'clean-minimal', skin: 'blue-minimal',  label: 'Clean Minimal',  ats: true,
    category: 'Creative',
    roles: ['UX Designer','Brand Designer','Content Creator','Art Director','Motion Designer'],
    hBg: '#ffffff',  hTx: '#4c1d95', ac: '#7c3aed', bodyBg: '#fafafa',
    cardStyle: 'none', fontFamily: 'sans', centerName: true,
  },
  {
    id: 'creative-bold', skin: 'black-clean',   label: 'Creative',       ats: true,
    category: 'Creative',
    roles: ['Graphic Designer','Creative Director','Illustrator','UI Designer','Visual Designer'],
    hBg: '#f5f3ff', hTx: '#3b1f6e', ac: '#8b5cf6', bodyBg: '#fdf4ff',
    cardStyle: 'rounded-soft', fontFamily: 'sans',
  },
  {
    id: 'marketing-pro', skin: 'blue-modern',   label: 'Marketing Pro',  ats: true,
    category: 'Creative',
    roles: ['Digital Marketer','SEO Specialist','Copywriter','Social Media Manager','Growth Hacker'],
    hBg: '#ede9fe', hTx: '#5b21b6', ac: '#7c3aed', bodyBg: '#faf5ff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  // ── Finance ────────────────────────────────────────────────────────────────
  {
    id: 'finance-pro',   skin: 'navy-compact',  label: 'Finance Pro',    ats: true,
    category: 'Finance',
    roles: ['Investment Banker','Financial Analyst','Portfolio Manager','Risk Analyst','Equity Research'],
    hBg: '#eef2ff', hTx: '#312e81', ac: '#4f46e5', bodyBg: '#f5f6ff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'accounting',    skin: 'blue-classic',  label: 'Accounting',     ats: true,
    category: 'Finance',
    roles: ['CPA','Accountant','Tax Specialist','Controller','Auditor','CFO'],
    hBg: '#dcfce7', hTx: '#14532d', ac: '#16a34a', bodyBg: '#f0fdf4',
    cardStyle: 'rounded-accent', fontFamily: 'sans',
  },
  // ── Healthcare ─────────────────────────────────────────────────────────────
  {
    id: 'healthcare-pro', skin: 'blue-classic', label: 'Healthcare Pro', ats: true,
    category: 'Healthcare',
    roles: ['Physician','Surgeon','Specialist','Medical Officer','Clinical Director'],
    hBg: '#e0f2fe', hTx: '#0c4a6e', ac: '#0284c7', bodyBg: '#f0f9ff',
    cardStyle: 'rounded-accent', fontFamily: 'sans',
  },
  {
    id: 'nursing',       skin: 'slate-pro',     label: 'Nursing',        ats: true,
    category: 'Healthcare',
    roles: ['RN','BSN','ICU Nurse','Nurse Practitioner','CNS','Travel Nurse'],
    hBg: '#ecfeff', hTx: '#155e75', ac: '#0891b2', bodyBg: '#f0fdff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'health-admin',  skin: 'blue-modern',   label: 'Health Admin',   ats: true,
    category: 'Healthcare',
    roles: ['Healthcare Administrator','Medical Coder','Billing Specialist','Lab Technician','Pharmacist'],
    hBg: '#d1fae5', hTx: '#065f46', ac: '#059669', bodyBg: '#ecfdf5',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  // ── Entry Level ────────────────────────────────────────────────────────────
  {
    id: 'fresh-grad',    skin: 'blue-minimal',  label: 'Fresh Graduate', ats: true,
    category: 'Entry Level',
    roles: ['Recent Graduate','Junior Analyst','Trainee','Graduate Trainee','Intern to Full-Time'],
    hBg: '#ffffff',  hTx: '#1d4ed8', ac: '#3b82f6', bodyBg: '#fafafa',
    cardStyle: 'none', fontFamily: 'sans', centerName: true,
  },
  {
    id: 'junior-tech',   skin: 'navy-compact',  label: 'Junior Tech',    ats: true,
    category: 'Entry Level',
    roles: ['Junior Developer','Associate Engineer','Entry-Level Data Analyst','IT Support','QA Trainee'],
    hBg: '#dbeafe', hTx: '#1e3a5f', ac: '#3b82f6', bodyBg: '#eff6ff',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'entry-business', skin: 'blue-classic', label: 'Entry Business', ats: true,
    category: 'Entry Level',
    roles: ['Junior Business Analyst','Admin Assistant','Customer Success','HR Trainee','Sales Associate'],
    hBg: '#e0e7ff', hTx: '#3730a3', ac: '#6366f1', bodyBg: '#eef2ff',
    cardStyle: 'rounded-accent', fontFamily: 'sans',
  },
  // ── Legal ──────────────────────────────────────────────────────────────────
  {
    id: 'classic-serif', skin: 'black-serif',   label: 'Classic Serif',  ats: true,
    category: 'Legal',
    roles: ['Attorney','Legal Counsel','Solicitor','Barrister','In-House Counsel'],
    hBg: '#ffffff',  hTx: '#111827', ac: '#374151', bodyBg: '#ffffff',
    cardStyle: 'double-rule', fontFamily: 'serif', centerName: true,
  },
  {
    id: 'legal-pro',     skin: 'black-clean',   label: 'Legal Pro',      ats: true,
    category: 'Legal',
    roles: ['Corporate Lawyer','Paralegal','Compliance Officer','Legal Analyst','Contract Specialist'],
    hBg: '#fef3c7', hTx: '#1c1917', ac: '#b45309', bodyBg: '#fffbeb',
    cardStyle: 'rounded-soft', fontFamily: 'serif',
  },
  // ── Academic ───────────────────────────────────────────────────────────────
  {
    id: 'research-scholar', skin: 'black-serif', label: 'Research Scholar', ats: true,
    category: 'Academic',
    roles: ['PhD Candidate','Postdoctoral Researcher','Research Associate','Scientist','Lab Director'],
    hBg: '#e0e7ff', hTx: '#1e1b4b', ac: '#4f46e5', bodyBg: '#fafafa',
    cardStyle: 'double-rule', fontFamily: 'serif', centerName: true,
  },
  {
    id: 'faculty',       skin: 'mono-clean',    label: 'Faculty / CV',   ats: true,
    category: 'Academic',
    roles: ['Professor','Lecturer','Associate Professor','Department Head','Academic Dean'],
    hBg: '#f8fafc',  hTx: '#1e293b', ac: '#475569', bodyBg: '#ffffff',
    cardStyle: 'mono-border', fontFamily: 'serif',
  },
  {
    id: 'academic-clean', skin: 'blue-classic', label: 'Academic Clean', ats: true,
    category: 'Academic',
    roles: ['Research Fellow','Teaching Assistant','Academic Advisor','Curriculum Designer','Librarian'],
    hBg: '#f3e8ff', hTx: '#3b0764', ac: '#9333ea', bodyBg: '#faf5ff',
    cardStyle: 'rounded-accent', fontFamily: 'serif',
  },
  // ── Light / Soft ─────────────────────────────────────────────────────────
  {
    id: 'soft-white',    skin: 'soft-white',    label: 'Soft White',     ats: true,
    category: 'Business',
    roles: ['Any Role','General Professional','Consultant','Strategist','Manager'],
    hBg: '#f9fafb', hTx: '#374151', ac: '#9ca3af', bodyBg: '#ffffff',
    cardStyle: 'rounded-soft', fontFamily: 'sans',
  },
  {
    id: 'warm-cream',    skin: 'warm-cream',    label: 'Warm Cream',     ats: true,
    category: 'Legal',
    roles: ['Attorney','Legal Counsel','Academic','Consultant','Advisor'],
    hBg: '#f5f0e8', hTx: '#44372b', ac: '#c4a882', bodyBg: '#fffdf9',
    cardStyle: 'rounded-soft', fontFamily: 'serif',
  },
  {
    id: 'sage-light',    skin: 'sage-light',    label: 'Sage',           ats: true,
    category: 'Healthcare',
    roles: ['Healthcare','Wellness','Environment','Non-Profit','Social Work'],
    hBg: '#f0f7f0', hTx: '#2d5a2d', ac: '#86b886', bodyBg: '#f8fdf8',
    cardStyle: 'left-accent', fontFamily: 'sans',
  },
  {
    id: 'pearl-rose',    skin: 'pearl-rose',    label: 'Pearl',          ats: true,
    category: 'Creative',
    roles: ['HR Manager','Event Planner','PR Manager','Fashion','Lifestyle Brand'],
    hBg: '#fdf2f3', hTx: '#5a2028', ac: '#d87a88', bodyBg: '#fff8f9',
    cardStyle: 'rounded-soft', fontFamily: 'sans',
  },
];

/** Featured templates shown in the sidebar (first 4) */
export const RESULTS_TEMPLATES = TEMPLATE_LIBRARY.slice(0, 4) as { id: string; label: string; ats: boolean; labelKey?: string }[];

/** Map template id → CSS skin class (derived from TEMPLATE_LIBRARY for single source of truth) */
const TEMPLATE_ID_TO_SKIN: Record<string, string> = Object.fromEntries(
  TEMPLATE_LIBRARY.map(t => [t.id, t.skin])
);
export function getTemplateSkinClass(id: string): string {
  return TEMPLATE_ID_TO_SKIN[id] ?? 'blue-classic';
}

/**
 * Renders a full-detail resume document at 220×310px, scaled down in the thumbnail.
 * Each template uses its own accent color prominently throughout so users can
 * clearly see the color variation between templates.
 */
export function ResumeDocumentContent({ cfg }: { cfg: TemplateConfig }) {
  const { hBg, hTx, ac, bodyBg, cardStyle, fontFamily, centerName } = cfg;
  const dark  = '#111827';
  const muted = '#6b7280';
  const fontStack =
    fontFamily === 'serif' ? 'Georgia, "Times New Roman", serif' :
    fontFamily === 'mono'  ? '"Cascadia Code", "Courier New", monospace' :
    'system-ui, -apple-system, Arial, sans-serif';

  // Section header: accent color text + thick accent underline — highly visible at small size
  const secHead = (title: string) => {
    if (cardStyle === 'double-rule') return (
      <div style={{ fontSize: 7.5, fontWeight: 800, color: dark, letterSpacing: '0.12em', textTransform: 'uppercase' as const, fontFamily: fontStack, borderBottom: `2.5px double ${dark}`, paddingBottom: 3, marginBottom: 5 }}>{title}</div>
    );
    if (cardStyle === 'mono-border') return (
      <div style={{ fontSize: 7.5, fontWeight: 800, color: dark, letterSpacing: '0.12em', textTransform: 'uppercase' as const, fontFamily: fontStack, borderBottom: `2px solid ${dark}`, paddingBottom: 3, marginBottom: 5 }}>{title}</div>
    );
    return (
      <div style={{ fontSize: 7.5, fontWeight: 800, color: ac, letterSpacing: '0.12em', textTransform: 'uppercase' as const, fontFamily: fontStack, borderBottom: `2.5px solid ${ac}`, paddingBottom: 3, marginBottom: 5 }}>{title}</div>
    );
  };

  // Section wrapper: per-skin card treatment
  const secWrap = (children: React.ReactNode) => {
    const base: React.CSSProperties = { marginBottom: 9, fontFamily: fontStack };
    switch (cardStyle) {
      case 'rounded-accent': return <div style={{ ...base, border: `1px solid ${ac}44`, borderRadius: 4, padding: '7px 9px' }}>{children}</div>;
      case 'left-accent':    return <div style={{ ...base, borderLeft: `3px solid ${ac}`, paddingLeft: 8 }}>{children}</div>;
      case 'sharp':          return <div style={{ ...base, border: `1.5px solid ${dark}`, padding: '7px 9px' }}>{children}</div>;
      case 'rounded-soft':   return <div style={{ ...base, border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px' }}>{children}</div>;
      default:               return <div style={{ ...base }}>{children}</div>;
    }
  };

  const sec = (title: string, children: React.ReactNode) =>
    secWrap(<>{secHead(title)}{children}</>);

  // Date shown as an accent-colored pill — makes accent color prominent even in small text
  const datePill = (d: string) => (
    <span style={{ fontSize: 6, fontWeight: 700, color: '#fff', background: ac, padding: '1.5px 5px', borderRadius: 3, flexShrink: 0, marginLeft: 5, letterSpacing: '0.02em' }}>{d}</span>
  );

  const job = (title: string, company: string, dates: string, bullets: string[]) => (
    <div style={{ marginBottom: 7, fontFamily: fontStack }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
        <span style={{ fontSize: 8, fontWeight: 700, color: dark }}>{title}</span>
        {datePill(dates)}
      </div>
      <div style={{ fontSize: 7, color: muted, marginBottom: 3 }}>{company}</div>
      {bullets.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
          <span style={{ color: ac, fontWeight: 900, flexShrink: 0, fontSize: 9, lineHeight: '10px' }}>•</span>
          <span style={{ fontSize: 7, color: dark, lineHeight: 1.45 }}>{b}</span>
        </div>
      ))}
    </div>
  );

  const mainBody = (
    <>
      {sec('Professional Summary',
        <div style={{ fontSize: 7, color: dark, lineHeight: 1.5, fontFamily: fontStack }}>
          Results-driven professional with 8+ years leading cross-functional teams and exceeding KPIs by 30%+. Proven track record of on-time delivery.
        </div>
      )}
      {sec('Experience',
        <>
          {job('Senior Manager', 'Acme Corporation · Chicago, IL', 'Jan 2020 – Present', [
            'Increased revenue by 32% YoY through strategic initiatives',
            'Led team of 12, reducing delivery time by 40%',
            'Managed $2.4M budget with 98% utilization efficiency',
          ])}
          {job('Project Lead', 'Global Solutions Inc · New York', 'Mar 2016 – Dec 2019', [
            'Spearheaded product launch across 6 international markets',
            'Collaborated with C-suite on quarterly roadmaps & OKRs',
          ])}
        </>
      )}
      {sec('Education',
        <div style={{ fontFamily: fontStack }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <span style={{ fontSize: 7.5, fontWeight: 700, color: dark, flex: 1 }}>MBA, Business Administration</span>
            {datePill('2016')}
          </div>
          <div style={{ fontSize: 7, color: muted, marginBottom: 6 }}>Harvard Business School, Cambridge</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <span style={{ fontSize: 7.5, fontWeight: 700, color: dark, flex: 1 }}>B.Sc, Computer Science</span>
            {datePill('2013')}
          </div>
          <div style={{ fontSize: 7, color: muted }}>MIT, Cambridge, MA</div>
        </div>
      )}
      {sec('Skills',
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {['Leadership','Strategy','Python','SQL','Agile','Data Analysis','Budgeting','Stakeholder Mgmt'].map(s => (
            <span key={s} style={{ fontSize: 6, background: `${ac}22`, color: ac, padding: '2px 5px', borderRadius: 3, fontWeight: 700 }}>{s}</span>
          ))}
        </div>
      )}
      {sec('Certifications',
        <div style={{ fontFamily: fontStack }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <span style={{ fontSize: 7, color: dark, flex: 1 }}>PMP — Project Management Professional</span>
            {datePill('2021')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 7, color: dark, flex: 1 }}>AWS Solutions Architect Associate</span>
            {datePill('2020')}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div style={{ background: bodyBg, display: 'flex', flexDirection: 'column', width: '100%', height: '100%', fontFamily: fontStack }}>
      {/* Header */}
      <div style={{
        background: hBg, padding: '13px 16px', flexShrink: 0,
        borderBottom: (cardStyle === 'double-rule' || cardStyle === 'mono-border') ? `2px solid ${dark}` : `3px solid ${ac}`,
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: hTx, letterSpacing: '0.03em', lineHeight: 1.1, fontFamily: fontStack, textAlign: centerName ? 'center' : 'left' }}>
          JOHN SMITH
        </div>
        <div style={{ fontSize: 9, fontWeight: 600, color: hTx, opacity: 0.85, marginTop: 3, fontFamily: fontStack, textAlign: centerName ? 'center' : 'left' }}>
          Senior Manager &amp; Strategy Lead
        </div>
        <div style={{ fontSize: 7, color: hTx, opacity: 0.65, marginTop: 5, display: 'flex', gap: 8, flexWrap: 'wrap', fontFamily: fontStack, justifyContent: centerName ? 'center' : 'flex-start' }}>
          <span>john@email.com</span><span>+1 555 0100</span><span>linkedin.com/in/john</span><span>Chicago, IL</span>
        </div>
      </div>
      <div style={{ flex: 1, padding: '12px 16px', background: bodyBg, overflow: 'hidden' }}>
        {mainBody}
      </div>
    </div>
  );
}

/** Render a single realistic resume thumbnail using a scaled-down full resume document */
export function TemplateThumbnail({ tpl, selected, onClick, compact = false }: {
  tpl: { id: string; label: string; ats?: boolean };
  selected: boolean;
  onClick: () => void;
  /** Denser preview + label for grid layouts (e.g. Results template card) */
  compact?: boolean;
}) {
  const cfg = TEMPLATE_LIBRARY.find(t => t.id === tpl.id) ?? TEMPLATE_LIBRARY[0];

  const RENDER_W = 220;
  const RENDER_H = 310;
  const SCALE = compact ? 0.38 : 0.59;
  const thumbH = Math.round(RENDER_H * SCALE);

  return (
    <button
      type="button"
      onClick={onClick}
      title={tpl.label}
      className={`w-full overflow-hidden border-2 text-left transition-all focus:outline-none ${
        compact ? 'rounded-lg' : 'rounded-xl'
      } ${
        selected
          ? 'border-[#6C47FF] ring-2 ring-[#6C47FF]/30 shadow-md'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 hover:shadow-sm'
      }`}
      style={{ transition: 'all 0.15s ease' }}
    >
      {/* Scaled document container */}
      <div style={{ height: thumbH, overflow: 'hidden', position: 'relative', background: '#ffffff' }}>
        <div style={{
          width: RENDER_W,
          height: RENDER_H,
          transform: `scale(${SCALE})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <ResumeDocumentContent cfg={cfg} />
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: compact ? 14 : 24,
            background: 'linear-gradient(to bottom, transparent, #ffffff)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Label bar */}
      <div
        className={`flex items-center justify-between border-t ${
          compact ? 'px-1.5 py-1' : 'px-2.5 py-1.5'
        } ${selected ? 'bg-[#6C47FF]/8 border-[#6C47FF]/25' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}
      >
        <span
          className={`truncate font-semibold leading-tight ${
            compact ? 'text-[9px]' : 'text-[11px]'
          } ${selected ? 'text-[#6C47FF]' : 'text-gray-700 dark:text-gray-300'}`}
        >
          {tpl.label}
        </span>
        {tpl.ats && (
          <span
            className={`rounded font-bold tracking-wider shrink-0 ${
              compact ? 'ml-1 px-1 py-0 text-[7px]' : 'ml-1.5 px-1.5 py-0.5 text-[9px]'
            } ${selected ? 'bg-[#6C47FF] text-white' : 'bg-emerald-500 text-white'}`}
          >
            ATS
          </span>
        )}
      </div>
    </button>
  );
}

/** Template Library Modal */
const LIBRARY_CATEGORIES = ['All', 'Tech', 'Business', 'Creative', 'Finance', 'Healthcare', 'Entry Level', 'Legal', 'Academic'];

export function TemplateLibraryModal({ selectedId, onSelect, onClose }: {
  selectedId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const [activeCategory, setActiveCategory] = React.useState('All');
  const [search, setSearch] = React.useState('');

  const filtered = TEMPLATE_LIBRARY.filter(t => {
    const matchCat = activeCategory === 'All' || t.category === activeCategory;
    const q = search.toLowerCase();
    const matchSearch = !q || t.label.toLowerCase().includes(q) || t.roles.some(r => r.toLowerCase().includes(q)) || t.category.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700" style={{ background: 'var(--brand-cta-bg)' }}>
          <div>
            <h2 className="text-base font-bold text-white">Template Library</h2>
            <p className="text-xs text-white/70 mt-0.5">Choose an ATS-compliant template by role</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Search + category tabs */}
        <div className="flex-shrink-0 px-5 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by role (e.g. Software Engineer, Designer…)"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#6C47FF] mb-3"
          />
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5 flex-wrap">
            {LIBRARY_CATEGORIES.map(cat => (
              <button key={cat} type="button" onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  activeCategory === cat
                    ? 'bg-[#6C47FF] text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template grid */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-2 opacity-40"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <p className="text-sm">No templates found for "{search}"</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(tpl => (
                <div key={tpl.id} className="flex flex-col gap-1">
                  <TemplateThumbnail
                    tpl={{ id: tpl.id, label: tpl.label, ats: tpl.ats }}
                    selected={selectedId === tpl.id}
                    onClick={() => onSelect(tpl.id)}
                  />
                  <div className="flex flex-wrap gap-1 px-0.5">
                    {tpl.roles.slice(0,2).map(r => (
                      <span key={r} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{r}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">{filtered.length} templates · All ATS-compliant</p>
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: 'var(--brand-cta-bg)' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** Renders resume as kickresume-style section cards (PROFILE, WORK EXPERIENCE, SKILLS, etc.) or fallback. */
function PreviewResumeContent({ text, sections: sectionsProp, onSectionClick }: { text: string; sections?: ResumeSection[]; onSectionClick?: (index: number) => void }) {
  const normalized = normalizeResumeText(text);
  const sections = sectionsProp ?? parseResumeSections(normalized);
  const useSectionCards = sections.length > 1 && sections.some(s => s.title !== 'Full Resume');

  if (useSectionCards) {
    return (
      <div className="space-y-3 w-full max-w-full">
        {sections.map((section, i) => {
          const displayTitle = (i === 0 && isFirstSectionPersonalInfo(section.title)) ? 'Personal Information' : section.title;
          return (
            <SectionCard key={`${section.title}-${i}`} section={section} sectionIndex={i} onSectionClick={onSectionClick} displayTitle={displayTitle} />
          );
        })}
      </div>
    );
  }

  const lines = normalized.split(/\n/);
  const content = lines.map((line, i) => {
    const key = `line-${i}`;
    const trimmed = line.trim();
    if (!trimmed) return <div key={key} className="h-2" aria-hidden />;
    if (isSectionHeader(line)) {
      return (
        <div key={key} className="mt-4 first:mt-0">
          <div className="font-semibold text-gray-900 dark:text-white text-[15px] border-b border-gray-200 dark:border-gray-600 pb-0.5">{line}</div>
        </div>
      );
    }
    if (isBulletLine(line)) {
      return (
        <div key={key} className="flex gap-2 pl-0 mt-0.5">
          <span className="resume-preview-bullet text-[#6C47FF] dark:text-[#5B8DFF] font-bold shrink-0">•</span>
          <span className="text-gray-800 dark:text-gray-200 resume-preview-content">{renderInlineFormatting(bulletContent(line), key)}</span>
        </div>
      );
    }
    return (
      <div key={key} className="mt-0.5 text-gray-800 dark:text-gray-200 leading-relaxed resume-preview-content">
        {renderInlineFormatting(line, key)}
      </div>
    );
  });
  return <div className="text-[15px] leading-[1.6] font-sans w-full max-w-full">{content}</div>;
}


interface ResultsStepProps {
  optimizationHook: {
    optimizationResults: any[];
    currentResult: any;
    resetOptimization: () => void;
  };
  resumeText: string;
  onRestart: () => void;
  onBack?: () => void;
  /** Guest view: show watermark and signup CTA to download */
  isGuest?: boolean;
  /** Called when guest taps "Sign up to download" */
  onSignup?: () => void;
  /** Optional job context for verified ATS score and for "Improve with AI" per section */
  jobData?: { title?: string; company?: string; skills?: string[]; requirements?: string[]; description?: string };
  /** Selected template for preview skin (from flow or local) */
  selectedTemplateId?: string;
  /** When user picks a template in Results, notify parent */
  onTemplateChange?: (templateId: string) => void;
  /** When user changes design settings (font/color/caps), notify parent so history is saved correctly */
  onDesignChange?: (design: { font: string; color: string; caps: string }) => void;
  /** B2B RoleFit: flow originated from candidate profile */
  isRoleFitFlow?: boolean;
  /** Generate and download PDF (parent handles auth / errors) */
  onDownloadPdf?: () => void | Promise<void>;
  downloadPdfBusy?: boolean;
  /** RoleFit: persist resume file to source candidate (`roleFitPrefetch.candidateId`) + navigate away (parent). Body includes template metadata when serialized. */
  onSaveToProfile?: (fileContent: string) => void | Promise<void>;
  /** Saved with profile file so Fitment can restore template + design (optional). */
  profileSaveDesign?: SavedProfileDesign;
  saveToProfileBusy?: boolean;
  /** Standard optimizer: go to Save History step */
  onContinueToHistory?: () => void;
}

const ResultsStep: React.FC<ResultsStepProps> = ({
  optimizationHook,
  resumeText,
  onRestart,
  onBack,
  isGuest = false,
  onSignup,
  jobData,
  selectedTemplateId: selectedTemplateIdProp,
  onTemplateChange,
  onDesignChange,
  isRoleFitFlow = false,
  onDownloadPdf,
  downloadPdfBusy = false,
  onSaveToProfile,
  saveToProfileBusy = false,
  onContinueToHistory,
  profileSaveDesign,
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const result = optimizationHook.currentResult;

  const [selectedTemplateIdState, setSelectedTemplateIdState] = useState(selectedTemplateIdProp || 'ats-optimized');
  const selectedTemplateId = selectedTemplateIdProp ?? selectedTemplateIdState;
  const [savedTemplateId, setSavedTemplateId] = useState(selectedTemplateIdProp || 'ats-optimized');
  const [templateSaved, setTemplateSaved] = useState(false);
  const [mobileShowTemplates, setMobileShowTemplates] = useState(false);

  const handleDownloadPdfClick = useCallback(async () => {
    if (isGuest && onSignup) {
      onSignup();
      return;
    }
    await onDownloadPdf?.();
  }, [isGuest, onSignup, onDownloadPdf]);

  // ── Native design state ──────────────────────────────────────────────────
  const [nativeActivePanel, setNativeActivePanel] = useState<'design'|null>(null);
  const [nativeDesignExpanded, setNativeDesignExpanded] = useState(false);
  const [nativeDesignColor, setNativeDesignColor] = useState('');
  const [nativeDesignFont, setNativeDesignFont] = useState<'default'|'serif'|'compact'>('default');
  const [nativeDesignCaps, setNativeDesignCaps] = useState<'none'|'title'|'upper'>('none');

  // Notify parent whenever design changes so history is saved with correct styling
  const handleNativeDesignFont = useCallback((f: 'default'|'serif'|'compact') => {
    setNativeDesignFont(f);
    onDesignChange?.({ font: f, color: nativeDesignColor, caps: nativeDesignCaps });
  }, [nativeDesignColor, nativeDesignCaps, onDesignChange]);
  const handleNativeDesignColor = useCallback((c: string) => {
    setNativeDesignColor(c);
    onDesignChange?.({ font: nativeDesignFont, color: c, caps: nativeDesignCaps });
  }, [nativeDesignFont, nativeDesignCaps, onDesignChange]);
  const handleNativeDesignCaps = useCallback((caps: 'none'|'title'|'upper') => {
    setNativeDesignCaps(caps);
    onDesignChange?.({ font: nativeDesignFont, color: nativeDesignColor, caps });
  }, [nativeDesignFont, nativeDesignColor, onDesignChange]);
  const nativeDragStartY = useRef<number|null>(null);
  const totalSidebarTemplates = TEMPLATE_LIBRARY.length;

  const optimizedText = result?.optimizedResume || result?.final_resume || result?.optimized_resume || '';

  // Gap analysis — computed client-side from resume + JD data already available as props.
  // Prefer an already-computed version attached to result (from the optimization pipeline),
  // or fall back to computing it fresh. This runs even if the result was restored from
  // sessionStorage where the gapAnalysis field may not have been serialized.
  const gapAnalysis = useMemo((): GapAnalysis | null => {
    // Prefer already-computed version on result
    const attached = (result as any)?.gapAnalysis as GapAnalysis | undefined;
    if (attached && attached.coverageScore !== undefined) return attached;
    // Compute fresh if we have enough JD data
    const hasJD = jobData && (
      (jobData.description && jobData.description.trim().length > 30) ||
      (jobData.skills && jobData.skills.length > 0) ||
      (jobData.requirements && jobData.requirements.length > 0)
    );
    const sourceText = optimizedText || resumeText;
    if (!hasJD || !sourceText || sourceText.length < 50) return null;
    try {
      const jdFull = {
        title: jobData!.title || '',
        company: jobData!.company || '',
        location: '',
        responsibilities: [],
        requirements: jobData!.requirements || [],
        skills: jobData!.skills || [],
        description: jobData!.description || '',
      };
      const jdStructure = extractJDStructure(jdFull);
      return analyzeGaps(sourceText, jdStructure);
    } catch {
      return null;
    }
  }, [result, resumeText, optimizedText, jobData]);

  // ATS score: use only real values from optimization; no fake fallbacks (75/50)
  const extractedAtsScore = result?.optimizedScore ?? result?.match_score ?? result?.optimized_score ?? result?.atsScore?.after ?? 0;
  const extractedInitialScore = result?.originalScore ?? result?.initial_score ?? result?.initialScore ?? result?.atsScore?.before ?? 0;
  const atsScore = extractedAtsScore > 0 ? extractedAtsScore : (result?.afterScores?.overall ?? 0);
  const initialScore = extractedInitialScore > 0 ? extractedInitialScore : (result?.beforeScores?.overall ?? 0);

  const verifiedAtsScore = useMemo(() => {
    if (!optimizedText || !jobData) return null;
    try {
      const score = calculateJobMatchScore(
        optimizedText,
        jobData.skills || [],
        jobData.description || ''
      );
      return Math.round(score);
    } catch {
      return null;
    }
  }, [optimizedText, jobData]);

  const displayAtsScore = verifiedAtsScore ?? (atsScore > 0 ? atsScore : 0);
  const displayInitialScore = initialScore > 0 ? initialScore : 0;
  const hasDisplayScore = displayAtsScore > 0;

  // True when the optimization result carries per-section data (produced by ProcessingStep)
  const hasLLMSections = useMemo(() => {
    const llm = (result as any)?.sections;
    return Array.isArray(llm) && llm.length > 0;
  }, [result]);

  const sections = useMemo(() => {
    // Priority 1: use pre-parsed sections from LLM per-section optimization.
    // IMPORTANT: do NOT run bucketResumeSections() here — that function runs
    // redistributeMisplacedContent which classifies every line from scratch and
    // scrambles already-clean LLM output. Just canonicalize titles and sort.
    const llmSections = (result as any)?.sections as Array<{title: string; content: string; preview?: string}> | undefined;
    if (Array.isArray(llmSections) && llmSections.length > 0) {
      const mapped = llmSections.map(s => ({
        title: s.title || '',
        content: cleanLLMSectionContent(s.content || ''),
        preview: s.preview || (s.content || '').slice(0, 120).replace(/\n/g, ' '),
      })) as ResumeSection[];
      return sortSectionsByCanonicalOrder(mapped.filter(s => s.content.trim().length > 0));
    }
    // Priority 2: fallback to regex parse (legacy / LLM didn't return sections)
    if (!optimizedText) return [];
    return parseResumeSections(normalizeResumeText(optimizedText));
  }, [result, optimizedText]);

  const originalSections = useMemo(() => {
    if (!resumeText) return [];
    return parseResumeSections(normalizeResumeText(resumeText));
  }, [resumeText]);

  const [editableSections, setEditableSections] = useState<ResumeSection[]>([]);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [improvingSectionIndex, setImprovingSectionIndex] = useState<number | null>(null);
  const [improveError, setImproveError] = useState<string | null>(null);
  const [undoStacks, setUndoStacks] = useState<Record<number, string[]>>({});
  const [redoStacks, setRedoStacks] = useState<Record<number, string[]>>({});
  const [savedSectionIndex, setSavedSectionIndex] = useState<number | null>(null);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);

  // When optimized text changes, set editable sections.
  // If we have per-section LLM results (hasLLMSections), use them directly — they are already
  // correctly titled, sorted, and clean. Merging with original sections would re-introduce
  // original sections (Awards, Leadership & Activities, etc.) that the LLM didn't produce,
  // causing wrong order and garbage content in the template preview.
  // Only do the original-merge for the legacy full-text fallback path.
  useEffect(() => {
    if (sections.length === 0) return;
    setEditableSections(() => {
      // Per-section LLM results or no original → use optimized as-is
      if (hasLLMSections || originalSections.length === 0) return sections;

      // Legacy fallback: merge with original structure so section order matches source document.
      // Bucket original sections to get canonical names (merges skill sub-categories → "Skills", etc.)
      const bucketedOriginals = bucketResumeSections(originalSections);

      // Build lookup of optimized sections by canonical title
      const optimizedByTitle = new Map<string, ResumeSection>(
        sections.map(s => [s.title.toLowerCase().trim(), s])
      );

      // Walk bucketed originals in canonical order; replace with optimized where available
      const usedOptimizedTitles = new Set<string>();
      const merged = bucketedOriginals.map(s => {
        const key = s.title.toLowerCase().trim();
        const opt = optimizedByTitle.get(key);
        if (opt && !usedOptimizedTitles.has(key)) {
          usedOptimizedTitles.add(key);
          return opt;
        }
        return s;
      });

      // Append any new sections the LLM added that aren't covered by any original bucket
      const addedByLlm = sections.filter(s => !usedOptimizedTitles.has(s.title.toLowerCase().trim()));

      return [...merged, ...addedByLlm];
    });
  }, [optimizedText, sections, originalSections, hasLLMSections]);

  // Derived display sections — declared here so all subsequent hooks/callbacks can use it
  const displaySections = editableSections.length > 0 ? editableSections : sections;

  /** Plain text as shown in the preview (includes section edits) — used for “Save to profile”. */
  const buildPlainTextForCandidateProfile = useCallback(() => {
    const fromUi = reconstructStructuredResumeText(displaySections).trim();
    if (fromUi) return normalizeResumeText(fromUi).trim();
    return normalizeResumeText(optimizedText || '').trim();
  }, [displaySections, optimizedText]);

  const buildSerializedProfileFile = useCallback(() => {
    const body = buildPlainTextForCandidateProfile();
    return serializeSavedProfileResumeFile(
      {
        templateId: selectedTemplateId,
        design: profileSaveDesign,
      },
      body
    );
  }, [buildPlainTextForCandidateProfile, selectedTemplateId, profileSaveDesign]);

  // Keep selected section index in bounds when sections change
  useEffect(() => {
    if (displaySections.length > 0 && selectedSectionIndex >= displaySections.length) {
      setSelectedSectionIndex(Math.max(0, displaySections.length - 1));
    }
  }, [displaySections.length, selectedSectionIndex]);

  // Gamma: scroll right pane to selected section when user clicks left list
  useEffect(() => {
    const el = sectionRefs.current[selectedSectionIndex];
    if (el && rightScrollRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSectionIndex]);

  const handleSectionContentChange = useCallback((index: number, content: string) => {
    setEditableSections(prev => {
      const current = prev[index]?.content ?? '';
      if (current === content) return prev;
      setUndoStacks(u => ({ ...u, [index]: [...(u[index] || []), current] }));
      setRedoStacks(r => ({ ...r, [index]: [] }));
      return prev.map((s, i) => i === index ? { ...s, content, preview: content.slice(0, 100).replace(/\n/g, ' ') + (content.length > 100 ? '…' : '') } : s);
    });
  }, []);

  const handleUndo = useCallback((index: number, currentContent: string) => {
    const stack = undoStacks[index];
    if (!stack?.length) return;
    const previous = stack[stack.length - 1];
    setUndoStacks(u => ({ ...u, [index]: stack.slice(0, -1) }));
    setRedoStacks(r => ({ ...r, [index]: [...(r[index] || []), currentContent] }));
    setEditableSections(prev => prev.map((s, i) => i === index ? { ...s, content: previous, preview: previous.slice(0, 100).replace(/\n/g, ' ') + (previous.length > 100 ? '…' : '') } : s));
  }, [undoStacks]);

  const handleRedo = useCallback((index: number, currentContent: string) => {
    const stack = redoStacks[index];
    if (!stack?.length) return;
    const nextContent = stack[stack.length - 1];
    setRedoStacks(r => ({ ...r, [index]: stack.slice(0, -1) }));
    setUndoStacks(u => ({ ...u, [index]: [...(u[index] || []), currentContent] }));
    setEditableSections(prev => prev.map((s, i) => i === index ? { ...s, content: nextContent, preview: nextContent.slice(0, 100).replace(/\n/g, ' ') + (nextContent.length > 100 ? '…' : '') } : s));
  }, [redoStacks]);

  const handleSaveSection = useCallback((index: number) => {
    setSavedSectionIndex(index);
    setTimeout(() => setSavedSectionIndex(null), 2000);
  }, []);

  // Keep a ref so unmount cleanup can read the latest value without stale closure
  const selectedTemplateIdRef = useRef(selectedTemplateId);
  useEffect(() => { selectedTemplateIdRef.current = selectedTemplateId; }, [selectedTemplateId]);

  // Auto-save on unmount: last previewed template becomes saved
  useEffect(() => {
    return () => { onTemplateChange?.(selectedTemplateIdRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clicking a thumbnail immediately applies the template (auto-apply)
  const handleTemplateSelect = useCallback((id: string) => {
    setSelectedTemplateIdState(id);
    setTemplateSaved(false);
    onTemplateChange?.(id);
  }, [onTemplateChange]);

  // Explicit save = persist + show confirmation flash
  const handleSaveTemplate = useCallback(() => {
    setSavedTemplateId(selectedTemplateId);
    setTemplateSaved(true);
    onTemplateChange?.(selectedTemplateId);
    setTimeout(() => setTemplateSaved(false), 2000);
  }, [selectedTemplateId, onTemplateChange]);

  /** Call LLM to optimize a single section; update that section's content on success */
  const handleImproveSectionWithAi = useCallback(async (sectionIndex: number) => {
    const section = displaySections[sectionIndex];
    if (!section?.content?.trim()) return;
    setImproveError(null);
    setImprovingSectionIndex(sectionIndex);
    try {
      const sectionResumeText = `${section.title}\n\n${section.content}`;
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/optimize-resume`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          model: 'groq-llama-3.3-70b-versatile',
          resumeText: sectionResumeText,
          jobData: {
            title: jobData?.title || 'Target role',
            company: jobData?.company || 'Company',
            description: jobData?.description || '',
            skills: jobData?.skills || [],
            requirements: jobData?.requirements || [],
          },
          preferences: { tone: 'professional', verbosity: 'detailed' },
          bypassCredits: import.meta.env.VITE_BYPASS_CREDIT_SYSTEM === 'true',
        }),
        signal: AbortSignal.timeout(60000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || err?.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const optimizedFull = data?.optimizedContent || data?.optimizedResume || '';
      if (!optimizedFull?.trim()) throw new Error('Empty response from optimizer');
      // Use LLM sections if present, otherwise fall back to regex parse
      const rawLlmSections = data?.result?.sections ?? data?.sections;
      const parsed = (Array.isArray(rawLlmSections) && rawLlmSections.length > 0)
        ? rawLlmSections.map((s: {title: string; content: string; preview?: string}) => ({
            title: s.title || '',
            content: s.content || '',
            preview: s.preview || (s.content || '').slice(0, 120).replace(/\n/g, ' '),
          }))
        : parseResumeSections(normalizeResumeText(optimizedFull));
      const optimizedContent = parsed.length > 0
        ? (parsed.find(s => s.title.toLowerCase() === section.title.toLowerCase())?.content ?? parsed[0].content)
        : optimizedFull.trim();
      const preview = optimizedContent.slice(0, 100).replace(/\n/g, ' ') + (optimizedContent.length > 100 ? '…' : '');
      const previousContent = section.content ?? '';
      setUndoStacks(u => ({ ...u, [sectionIndex]: [...(u[sectionIndex] || []), previousContent] }));
      setRedoStacks(r => ({ ...r, [sectionIndex]: [] }));
      setEditableSections(prev => {
        const base = prev.length > 0 ? prev : displaySections;
        const next = base.map((s, i) =>
          i === sectionIndex ? { ...s, content: optimizedContent, preview } : s
        );
        return next;
      });
    } catch (e) {
      setImproveError(e instanceof Error ? e.message : 'Failed to improve section');
    } finally {
      setImprovingSectionIndex(null);
    }
  }, [displaySections, jobData]);

  const previewTextFromSections = displaySections.length > 0
    ? displaySections.map(s => `${s.title}\n${s.content}`).join('\n\n')
    : optimizedText;

  const documentTitle = useMemo(() => {
    if (!optimizedText) return t('optimizer.resultsStep.previewResume', 'Preview Resume');
    const firstLine = optimizedText.split(/\n/).find((line: string) => line.trim().length > 0)?.trim() || '';
    return firstLine.length > 40 ? t('optimizer.resultsStep.previewResume', 'Preview Resume') : firstLine || t('optimizer.resultsStep.previewResume', 'Preview Resume');
  }, [optimizedText, t]);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pageContainerRef = useRef<HTMLDivElement>(null);

  if (!result) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className={STEP_MOTION_CLASS}
      >
        <div className={STEP_WRAPPER_CLASS}>
          <div className={`${STEP_CARD_CLASS} items-center justify-center p-8 sm:p-12 text-center`}>
            <FileText className="w-16 h-16 vscode-text-tertiary mx-auto mb-4" />
            <h3 className="text-xl font-bold vscode-text-primary mb-2">{`[${t('optimizer.resultsStep.noResults')}]`}</h3>
            <p className="text-sm vscode-text-secondary mb-6">
              {t('optimizer.resultsStep.noResultsDesc')}
            </p>
            <button
              type="button"
              onClick={onRestart}
              className={`min-h-[44px] touch-manipulation ${STEP_PRIMARY_CTA_CLASS} px-6 py-3`}
            >
              {`[${t('optimizer.resultsStep.startOptimization')}]`}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     NATIVE MOBILE RENDER — Kickresume-style post-optimization view
     ─────────────────────────────────────────────────────────────────────── */
  if (isNative) {
    const skinClass = getTemplateSkinClass(selectedTemplateId);
    const nativeColorEntry = [
      { label: 'Slate',  swatch: '#94a3b8', accent: '#94a3b8', accentBg: '#f1f5f9', accentText: '#334155', accentBorder: '#cbd5e1' },
      { label: 'Gray',   swatch: '#9ca3af', accent: '#9ca3af', accentBg: '#f3f4f6', accentText: '#374151', accentBorder: '#d1d5db' },
      { label: 'Stone',  swatch: '#a8a29e', accent: '#a8a29e', accentBg: '#f5f5f4', accentText: '#44403c', accentBorder: '#d6d3d1' },
      { label: 'Sage',   swatch: '#86b886', accent: '#6aaa6a', accentBg: '#f0f7f0', accentText: '#2d5a2d', accentBorder: '#b8d8b8' },
      { label: 'Lavender', swatch: '#a78bfa', accent: '#a78bfa', accentBg: '#f5f3ff', accentText: '#5b21b6', accentBorder: '#ddd6fe' },
      { label: 'Rose',   swatch: '#fda4af', accent: '#fda4af', accentBg: '#fff1f2', accentText: '#9f1239', accentBorder: '#fecdd3' },
    ].find(c => c.swatch === nativeDesignColor);
    const nativeCssVars: React.CSSProperties = nativeColorEntry ? {
      '--resume-accent':        nativeColorEntry.accent,
      '--resume-accent-bg':     nativeColorEntry.accentBg,
      '--resume-accent-text':   nativeColorEntry.accentText,
      '--resume-accent-border': nativeColorEntry.accentBorder,
    } as React.CSSProperties : {};

    const scoreAfter  = result?.optimizedScore ?? result?.afterScores?.overall ?? 0;
    const scoreBefore = result?.originalScore  ?? result?.beforeScores?.overall ?? 0;
    const scoreDelta  = scoreAfter - scoreBefore;

    const NATIVE_DESIGN_COLORS = [
      '#94a3b8','#9ca3af','#a8a29e','#86b886','#a78bfa','#fda4af',
    ];

    return (
      /* absolute fill so parent overflow-y-auto doesn't clip our internal scroll */
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const }}>

        {/* ── Purple gradient header ── */}
        <div
          className="flex-shrink-0"
          style={{
            background: 'var(--ggj-header-bg)',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
            borderRadius: '0 0 24px 24px',
            boxShadow: 'var(--ggj-btn-shadow, 0 4px 16px rgba(0,0,0,0.12))',
          }}
        >
          <div className="flex items-center justify-between px-4 pb-3 gap-2">
            {/* Back */}
            <button type="button" onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {/* Title + score */}
            <div className="flex flex-col items-center flex-1 min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none" style={{ color: 'rgba(255,255,255,0.70)' }}>
                Resume Optimizer
              </span>
              <span className="text-sm font-bold leading-tight mt-0.5 text-white">
                ATS Optimized
              </span>
            </div>
            {/* Score chip + Download PDF */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {scoreAfter > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <TrendingUp className="w-3.5 h-3.5 text-green-300" />
                  <span className="text-xs font-bold text-white">{Math.round(scoreAfter)}%</span>
                  {scoreDelta > 0 && <span className="text-[10px] text-green-300">+{Math.round(scoreDelta)}</span>}
                </div>
              )}
              <button
                type="button"
                onClick={() => void handleDownloadPdfClick()}
                disabled={downloadPdfBusy}
                className="flex items-center gap-1 px-3 h-9 rounded-xl bg-white text-xs font-bold touch-manipulation shadow-sm disabled:opacity-60"
                style={{ color: 'var(--vscode-primary)' }}
              >
                {downloadPdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                [Download PDF]
              </button>
            </div>
          </div>
        </div>

        {/* ── Scrollable resume preview ── */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: 'var(--ggj-bg-page)', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
          <div className="pt-3 pb-6 px-3">
            <div
              className={[
                'resume-preview',
                `resume-preview--${skinClass}`,
                nativeDesignColor ? 'resume-preview--custom-accent' : '',
                nativeDesignFont === 'serif'   ? 'resume-preview--font-serif'   : '',
                nativeDesignFont === 'compact' ? 'resume-preview--font-compact' : '',
                `resume-preview--caps-${nativeDesignCaps}`,
              ].filter(Boolean).join(' ')}
              style={{
                background: '#ffffff',
                borderRadius: 14,
                boxShadow: '0 4px 28px rgba(0,0,0,0.13)',
                overflow: 'hidden',
                ...nativeCssVars,
              }}
            >
              {/* Use proper resume-preview CSS section cards so template skins apply */}
              <div className="resume-preview-document-inner p-4">
                {displaySections.length > 0 ? displaySections.map((sec, si) => (
                  <div key={si} className="resume-preview-section-wrapper mb-4">
                    <div className="resume-preview-section-card overflow-hidden">
                      <div className="resume-preview-section-header px-3 py-2">
                        <h4 className="resume-section-title text-xs font-bold uppercase tracking-widest">{sec.title}</h4>
                      </div>
                      <div className="resume-preview-section-body px-3 py-2">
                        <SectionContentWithFormatting content={sec.content} sectionTitle={sec.title} />
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="p-4 text-xs vscode-text-secondary whitespace-pre-wrap leading-relaxed">{previewTextFromSections}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Design drawer — always fully visible when active ── */}
        {nativeActivePanel === 'design' && (
          <div
            className="flex-shrink-0 border-t"
            style={{
              background: 'var(--ggj-bg-card)',
              borderColor: 'var(--ggj-border)',
              maxHeight: '60vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Drag handle */}
            <button type="button"
              className="w-full flex justify-center pt-2 pb-1 touch-manipulation flex-shrink-0"
              onTouchStart={(e) => { nativeDragStartY.current = e.touches[0].clientY; }}
              onTouchEnd={(e) => {
                if (nativeDragStartY.current === null) return;
                const delta = e.changedTouches[0].clientY - nativeDragStartY.current;
                nativeDragStartY.current = null;
                if (delta > 60) setNativeActivePanel(null);
              }}
              aria-label="Drag down to close"
            >
              <div className="w-10 h-1 rounded-full" style={{ background: 'var(--ggj-border)' }} />
            </button>

            <div className="overflow-y-auto flex-1 pb-2" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
              {/* ── Templates — ALL of them in horizontal scroll ── */}
              <div className="px-3 pt-1 pb-3">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: 'var(--ggj-text-muted)' }}>Templates</p>
                <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
                  {TEMPLATE_LIBRARY.map(tpl => {
                    const isSel = selectedTemplateId === tpl.id;
                    const cfg = TEMPLATE_LIBRARY.find(t => t.id === tpl.id) ?? TEMPLATE_LIBRARY[0];
                    // Scale a 180px × 240px document down to fit a ~80px wide card
                    const RENDER_W = 180;
                    const RENDER_H = 230;
                    const SCALE = 80 / RENDER_W;
                    const thumbH = Math.round(RENDER_H * SCALE);
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          setSelectedTemplateIdState(tpl.id);
                          onTemplateChange?.(tpl.id);
                        }}
                        className="flex-shrink-0 flex flex-col items-center gap-1 touch-manipulation"
                        style={{ width: 84 }}
                      >
                        <div style={{
                          width: 80,
                          height: thumbH,
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#ffffff',
                          border: isSel ? '2px solid var(--vscode-primary)' : '1.5px solid #e5e5e5',
                          boxShadow: isSel ? 'var(--ggj-btn-shadow)' : '0 1px 4px rgba(0,0,0,0.08)',
                          position: 'relative',
                          transition: 'all 0.15s',
                        }}>
                          <div style={{
                            width: RENDER_W,
                            height: RENDER_H,
                            transform: `scale(${SCALE})`,
                            transformOrigin: 'top left',
                            position: 'absolute',
                            top: 0, left: 0,
                            pointerEvents: 'none',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                          }}>
                            <ResumeDocumentContent cfg={cfg} />
                          </div>
                          {/* fade bottom */}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 16, background: 'linear-gradient(transparent, #fff)', pointerEvents: 'none' }} />
                          {isSel && (
                            <div style={{ position: 'absolute', top: 3, right: 3, width: 14, height: 14, borderRadius: '50%', background: '#6C47FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 600, color: isSel ? 'var(--vscode-primary)' : 'var(--ggj-text-muted)', width: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                          {tpl.label}
                        </span>
                        {tpl.ats && (
                          <span style={{ fontSize: 7, fontWeight: 700, background: isSel ? 'var(--vscode-primary)' : '#10b981', color: '#fff', padding: '1px 4px', borderRadius: 3, letterSpacing: '0.05em' }}>ATS</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Accent Color + Font + Headings — always shown ── */}
              <div className="px-3 pt-2 pb-3 space-y-4" style={{ borderTop: '1px solid var(--ggj-border)' }}>
                {/* Accent color */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ggj-text-muted)' }}>Accent Color</p>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => handleNativeDesignColor('')}
                      className="w-8 h-8 rounded-full border-2 flex items-center justify-center"
                      style={{ borderColor: !nativeDesignColor ? 'var(--vscode-primary)' : '#e5e5e5', background: '#f9f9f9' }}
                      title="Template default"
                    >
                      <X className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    {NATIVE_DESIGN_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => handleNativeDesignColor(c)}
                        className="w-8 h-8 rounded-full border-2"
                        style={{
                          background: c,
                          borderColor: nativeDesignColor === c ? 'var(--vscode-primary)' : 'transparent',
                          transform: nativeDesignColor === c ? 'scale(1.15)' : 'scale(1)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Font style */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ggj-text-muted)' }}>Font Style</p>
                  <div className="flex gap-2">
                    {(['default','serif','compact'] as const).map(f => (
                      <button key={f} type="button" onClick={() => handleNativeDesignFont(f)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold touch-manipulation capitalize"
                        style={{
                          background: nativeDesignFont === f ? 'var(--ggj-accent-gradient)' : 'var(--ggj-bg-page)',
                          color: nativeDesignFont === f ? '#fff' : 'var(--ggj-text-body)',
                          border: `1px solid ${nativeDesignFont === f ? 'var(--vscode-primary)' : 'var(--ggj-border)'}`,
                        }}
                      >{f}</button>
                    ))}
                  </div>
                </div>

                {/* Heading caps */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ggj-text-muted)' }}>Headings</p>
                  <div className="flex gap-2">
                  {([{v:'none',l:'Aa'},{v:'title',l:'Title'},{v:'upper',l:'AA'}] as const).map(o => (
                        <button key={o.v} type="button" onClick={() => handleNativeDesignCaps(o.v as typeof nativeDesignCaps)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold touch-manipulation"
                        style={{
                          background: nativeDesignCaps === o.v ? 'var(--ggj-accent-gradient)' : 'var(--ggj-bg-page)',
                          color: nativeDesignCaps === o.v ? '#fff' : 'var(--ggj-text-body)',
                          border: `1px solid ${nativeDesignCaps === o.v ? 'var(--vscode-primary)' : 'var(--ggj-border)'}`,
                        }}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom navigation ── */}
        <div
          className="flex-shrink-0 flex items-center border-t"
          style={{
            background: 'var(--ggj-nav-bg)',
            borderColor: 'var(--ggj-border)',
            backdropFilter: 'blur(12px)',
            paddingBottom: 12,
          }}
        >
          {/* Fill In */}
          <button type="button" onClick={() => setNativeActivePanel(null)}
            className="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1 touch-manipulation"
          >
            <AlignLeft className="w-5 h-5" style={{ color: nativeActivePanel === null ? '#6C47FF' : 'var(--ggj-tab-inactive)' }} />
            <span className="text-[10px] font-semibold" style={{ color: nativeActivePanel === null ? '#6C47FF' : 'var(--ggj-tab-inactive)' }}>
              [Preview]
            </span>
          </button>

          {/* Design */}
          <button type="button" onClick={() => setNativeActivePanel(p => p === 'design' ? null : 'design')}
            className="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1 touch-manipulation"
          >
            <Palette className="w-5 h-5" style={{ color: nativeActivePanel === 'design' ? '#6C47FF' : 'var(--ggj-tab-inactive)' }} />
            <span className="text-[10px] font-semibold" style={{ color: nativeActivePanel === 'design' ? '#6C47FF' : 'var(--ggj-tab-inactive)' }}>
              [Design]
            </span>
          </button>

          {/* Download PDF */}
          <button
            type="button"
            onClick={() => void handleDownloadPdfClick()}
            disabled={downloadPdfBusy}
            className="flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1 touch-manipulation disabled:opacity-50"
          >
            <div className="w-10 h-6 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#6C47FF,#4F35CC)' }}
            >
              {downloadPdfBusy ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Download className="w-3.5 h-3.5 text-white" />}
            </div>
            <span className="text-[10px] font-semibold text-[#6C47FF]">[Download PDF]</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={STEP_MOTION_CLASS}
    >
      <div className="w-full max-w-full flex-1 flex flex-col min-h-0 overflow-y-auto gap-0 scrollbar-hide">
        {/* Top strip — aligned with dark dashboard tokens */}
        <div
          className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b px-2 py-2 sm:px-3"
          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
        >
          <button
            type="button"
            onClick={onBack}
            aria-label={t('optimizer.resultsStep.backToProcessing')}
            className="flex shrink-0 touch-manipulation items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:opacity-90 min-h-[38px]"
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t('optimizer.resultsStep.backToProcessing')}</span>
          </button>

          <div className="h-6 w-px shrink-0" style={{ background: 'var(--vscode-border-primary)' }} />

          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="hidden shrink-0 text-xs sm:inline" style={{ color: 'var(--vscode-text-tertiary)' }}>Template:</span>
            <span className="truncate text-sm font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
              {(() => {
                const tpl = RESULTS_TEMPLATES.find(tp => tp.id === selectedTemplateId);
                return tpl?.label ?? selectedTemplateId;
              })()}
            </span>
            {selectedTemplateId !== savedTemplateId && !templateSaved && (
              <span
                className="hidden shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium sm:inline-flex"
                style={{
                  borderColor: 'var(--vscode-border-primary)',
                  color: 'var(--vscode-text-secondary)',
                  background: 'color-mix(in srgb, var(--vscode-text-primary) 6%, transparent)',
                }}
              >
                unsaved
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setFullScreenPreview(true)}
            className="hidden min-h-[38px] shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors hover:opacity-90 md:flex"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-secondary)',
              background: 'transparent',
            }}
          >
            <Maximize2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">[Preview]</span>
          </button>

          <button
            type="button"
            onClick={handleSaveTemplate}
            className={`hidden min-h-[38px] shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-all md:flex ${
              templateSaved ? 'bg-emerald-500 text-white' : `${STEP_PRIMARY_CTA_CLASS} text-white`
            }`}
          >
            {templateSaved ? (
              <>
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Saved!</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span className="hidden sm:inline">Save template</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => void handleDownloadPdfClick()}
            disabled={downloadPdfBusy}
            className="flex min-h-[38px] shrink-0 touch-manipulation items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-60 sm:px-4"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
              background: 'color-mix(in srgb, var(--vscode-text-primary) 5%, transparent)',
            }}
          >
            {downloadPdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span>[Download PDF]</span>
          </button>

          {isRoleFitFlow && onSaveToProfile && (
            <button
              type="button"
              onClick={() => void onSaveToProfile(buildSerializedProfileFile())}
              disabled={saveToProfileBusy}
              className={`flex min-h-[38px] shrink-0 touch-manipulation items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60 ${STEP_PRIMARY_CTA_CLASS} text-white`}
            >
              {saveToProfileBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="hidden sm:inline">{t('optimizer.resultsStep.saveToProfile', 'Save to profile')}</span>
            </button>
          )}

          {!isRoleFitFlow && onContinueToHistory && (
            <button
              type="button"
              onClick={onContinueToHistory}
              className={`flex min-h-[38px] shrink-0 touch-manipulation items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold ${STEP_PRIMARY_CTA_CLASS} text-white`}
            >
              <span className="hidden sm:inline">{t('optimizer.resultsStep.saveToHistory', 'Save to history')}</span>
              <ArrowRight className="h-4 w-4 sm:hidden" />
            </button>
          )}
        </div>

        <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto flex max-w-[1680px] min-h-0 flex-col gap-4 p-3 md:grid md:grid-cols-[minmax(280px,360px)_1fr] md:items-start md:gap-5 md:p-4">
            {/* Card: templates — compact grid (not a vertical strip) */}
            <div
              className="flex max-h-[min(520px,55vh)] flex-col overflow-hidden rounded-xl border md:max-h-[min(85vh,900px)]"
              style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
            >
              <div
                className="flex flex-shrink-0 items-center justify-between border-b px-3 py-2.5"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              >
                <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                  <Layout className="h-3.5 w-3.5 opacity-70" />
                  [ATS Templates]
                </span>
                <span className="text-[10px] font-medium tabular-nums" style={{ color: 'var(--vscode-primary)' }}>
                  {totalSidebarTemplates} designs
                </span>
              </div>
              <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto p-2 md:p-2.5">
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATE_LIBRARY.map(tpl => (
                    <div key={tpl.id} className="relative min-w-0">
                      <TemplateThumbnail
                        tpl={tpl}
                        compact
                        selected={selectedTemplateId === tpl.id}
                        onClick={() => handleTemplateSelect(tpl.id)}
                      />
                      {selectedTemplateId === tpl.id && (
                        <div
                          className="pointer-events-none absolute right-1 top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-[#6C47FF] shadow-sm ring-2 ring-white dark:ring-gray-800"
                          aria-hidden
                        >
                          <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card: match intelligence + resume */}
            <div
              className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border"
              style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
            >
              {gapAnalysis && (
                <div
                  className="flex-shrink-0 flex flex-wrap items-center gap-3 border-b px-3 py-2.5 text-xs md:px-4"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    background: 'color-mix(in srgb, var(--vscode-text-primary) 4%, var(--vscode-bg-elevated))',
                  }}
                >
                  <span className="mr-1 font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                    Match Intelligence
                  </span>
                  {gapAnalysis.strongMatches.length > 0 && (
                    <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/35 bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                      <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                      {gapAnalysis.strongMatches.length} strong match{gapAnalysis.strongMatches.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                  {gapAnalysis.weakMatches.length > 0 && (
                    <span className="flex items-center gap-1.5 rounded-full border border-amber-500/35 bg-amber-500/10 px-2 py-0.5 text-amber-800 dark:text-amber-200">
                      <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                      {gapAnalysis.weakMatches.length} improved
                    </span>
                  )}
                  {gapAnalysis.impliedMatches.length > 0 && (
                    <span className="flex items-center gap-1.5 rounded-full border border-blue-500/35 bg-blue-500/10 px-2 py-0.5 text-blue-800 dark:text-blue-200">
                      <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />
                      {gapAnalysis.impliedMatches.length} surfaced
                    </span>
                  )}
                  {gapAnalysis.missingSkills.length > 0 && (
                    <span
                      className="flex items-center gap-1.5 rounded-full border px-2 py-0.5"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-tertiary)',
                        background: 'color-mix(in srgb, var(--vscode-text-primary) 5%, transparent)',
                      }}
                    >
                      <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      {gapAnalysis.missingSkills.length} not in resume
                    </span>
                  )}
                  <span className="ml-auto font-semibold tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>
                    {gapAnalysis.coverageScore}%{' '}
                    <span className="font-normal" style={{ color: 'var(--vscode-text-tertiary)' }}>
                      JD coverage
                    </span>
                  </span>
                </div>
              )}

              <div
                ref={pageContainerRef}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
                style={{ background: 'var(--vscode-bg-tertiary)' }}
              >
                {displaySections.length > 0 ? (
                  <div className="flex flex-col items-center px-2 py-4 pb-8 md:px-4 md:py-8">
                    <div
                      ref={rightScrollRef}
                      className={`w-full max-w-[794px] bg-white text-slate-900 shadow-2xl resume-preview resume-preview--${getTemplateSkinClass(selectedTemplateId)}`}
                      data-template={selectedTemplateId}
                    >
                      <div className="resume-preview-document-inner">
                        {displaySections.map((section, i) => {
                          const isContact = i === 0 && isFirstSectionPersonalInfo(section.title);
                          const label = isContact
                            ? t('optimizer.resultsStep.personalInformation', 'Personal Information')
                            : section.title === 'Full Resume'
                              ? t('optimizer.resultsStep.fullResume', 'Full resume')
                              : section.title;
                          return (
                            <div
                              key={`section-${i}-${section.title}`}
                              ref={el => {
                                sectionRefs.current[i] = el;
                              }}
                              className="resume-preview-section-wrapper"
                            >
                              <div className={`resume-preview-section-card overflow-hidden${isContact ? ' resume-preview-contact-card' : ''}`}>
                                <div className="resume-preview-section-header">
                                  <h4 style={{ margin: 0 }}>{label.toUpperCase()}</h4>
                                </div>
                                <div className="resume-preview-section-body">
                                  <SectionContentWithFormatting content={section.content || ''} sectionTitle={section.title} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center p-8 text-center">
                    <div>
                      <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500 dark:text-amber-400" />
                      <p className="text-sm" style={{ color: 'var(--vscode-text-secondary)' }}>
                        {optimizedText ? t('optimizer.resultsStep.noSections', 'No sections') : t('optimizer.resultsStep.noOptimizedContent')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile sticky bar */}
        <div
          className="safe-area-inset-bottom flex flex-shrink-0 items-center gap-2 border-t px-2 py-2.5 md:hidden"
          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
        >
          <button
            type="button"
            onClick={() => setMobileShowTemplates(true)}
            className={`flex min-h-[44px] min-w-0 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${STEP_PRIMARY_CTA_CLASS}`}
          >
            <Layout className="h-4 w-4 shrink-0" />
            <span className="truncate">Templates</span>
          </button>
          <button
            type="button"
            onClick={() => void handleDownloadPdfClick()}
            disabled={downloadPdfBusy}
            className="flex min-h-[44px] shrink-0 touch-manipulation items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold disabled:opacity-60"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            {downloadPdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="max-w-[6.5rem] truncate text-[11px]">[Download PDF]</span>
          </button>
          <button
            type="button"
            onClick={() => setFullScreenPreview(true)}
            className="flex min-h-[44px] shrink-0 touch-manipulation items-center justify-center rounded-xl border px-3 py-2.5"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-secondary)',
            }}
            aria-label={t('optimizer.resultsStep.fullScreenPreview', 'Full screen preview')}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        {/* ── Mobile template bottom sheet ── */}
        {mobileShowTemplates && (
          <div
            className="md:hidden fixed inset-0 z-[9000] flex flex-col justify-end"
            role="dialog"
            aria-modal="true"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setMobileShowTemplates(false)}
            />
            {/* Sheet */}
            <div
              className="relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl max-h-[75vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="flex-shrink-0 flex justify-center pt-2.5 pb-1">
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>
              {/* Header */}
              <div
                className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b vscode-border"
                style={{ backgroundColor: 'var(--brand-popup-header)' }}
              >
                <span className="text-base font-semibold text-white flex items-center gap-2">
                  <Layout className="w-4 h-4 text-white/90" />
                  [ATS Templates]
                </span>
                <button
                  type="button"
                  onClick={() => setMobileShowTemplates(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Template grid — all templates */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {TEMPLATE_LIBRARY.map((tpl) => (
                    <div key={tpl.id}>
                      <TemplateThumbnail
                        tpl={tpl}
                        compact
                        selected={selectedTemplateId === tpl.id}
                        onClick={() => {
                          handleTemplateSelect(tpl.id);
                          setMobileShowTemplates(false);
                        }}
                      />
                      <p className="text-[8px] text-center text-gray-400 dark:text-gray-500 mt-0.5 truncate px-0.5">{tpl.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Apply button */}
              <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100 dark:border-gray-700 safe-area-inset-bottom">
                <button
                  type="button"
                  onClick={() => { handleSaveTemplate(); setMobileShowTemplates(false); }}
                  className={`min-h-[48px] w-full touch-manipulation text-sm font-semibold ${STEP_PRIMARY_CTA_CLASS}`}
                >
                  {templateSaved ? '✓ Template Saved!' : `[Apply Now] "${TEMPLATE_LIBRARY.find(t => t.id === selectedTemplateId)?.label ?? selectedTemplateId}"`}
                </button>
              </div>
            </div>
          </div>
        )}

          {/* Resume preview modal — does not close on backdrop click */}
          {fullScreenPreview && (
            <div
              className="fixed inset-0 z-[9998] flex items-start justify-center bg-black/60 backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              aria-label={t('optimizer.resultsStep.fullScreenPreview', 'Full screen preview')}
            >
              {/* Scrollable modal content */}
              <div
                className="relative mt-6 mb-6 w-full max-w-[680px] mx-4 overflow-y-auto overflow-x-hidden bg-gray-100 dark:bg-gray-900 rounded-xl shadow-2xl"
                style={{ maxHeight: 'calc(100vh - 3rem)' }}
                data-template={selectedTemplateId}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Title bar strip: context icon + title + close button (single color, no gradient) */}
                <div
                  className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 rounded-t-xl"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-white/80 leading-none mb-0.5 uppercase tracking-wide">
                      {(() => { const tpl = RESULTS_TEMPLATES.find(tp => tp.id === selectedTemplateId); return tpl?.label ?? selectedTemplateId; })()}
                    </p>
                    <h2 className="text-sm font-semibold text-white leading-tight truncate">
                      {t('optimizer.resultsStep.fullScreenPreview', 'Resume preview')}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFullScreenPreview(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 text-white hover:bg-white/25 transition-colors shrink-0"
                    aria-label={t('optimizer.resultsStep.exitFullScreen', 'Close preview')}
                  >
                    <X className="w-4 h-4" />
                    <span className="text-xs font-medium">Close</span>
                  </button>
                </div>

                {/* Resume content */}
                <div className="p-4">
                  <div
                    className={`w-full bg-white text-slate-900 shadow-xl resume-preview resume-preview--${getTemplateSkinClass(selectedTemplateId)}`}
                  >
                    <div className="resume-preview-document-inner">
                      {displaySections.map((section, i) => {
                        const isContact = i === 0 && isFirstSectionPersonalInfo(section.title);
                        const label = isContact
                          ? t('optimizer.resultsStep.personalInformation', 'Personal Information')
                          : section.title === 'Full Resume'
                            ? t('optimizer.resultsStep.fullResume', 'Full resume')
                            : section.title;
                        return (
                          <div key={`fs-section-${i}-${section.title}`} className="resume-preview-section-wrapper">
                            <div className={`resume-preview-section-card overflow-hidden${isContact ? ' resume-preview-contact-card' : ''}`}>
                              <div className="resume-preview-section-header">
                                <h4 style={{ margin: 0 }}>{label.toUpperCase()}</h4>
                              </div>
                              <div className="resume-preview-section-body">
                                <SectionContentWithFormatting content={section.content || ''} sectionTitle={section.title} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
    </motion.div>
  );
};

export default React.memo(ResultsStep);
